'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { addressSchema, type AddressFormData } from '@/lib/validations/address';
import { AddressesApi } from '@/lib/Apis/apis/addresses-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { ArrowLeft, MapPin } from 'lucide-react';
import { useNotification } from '@/providers/NotificationProvider';
import { US_STATES } from '@/lib/constants/us-states';

export default function NewAddressPage() {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const notification = useNotification();

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<AddressFormData>({
        resolver: zodResolver(addressSchema),
        defaultValues: {
            country: 'United States'
        }
    });

    const onSubmit = async (data: AddressFormData) => {
        setIsSubmitting(true);
        try {
            const api = new AddressesApi(AXIOS_CONFIG);
            const response = await api.createAddressForUser({
                name: data.name,
                street1: data.street1,
                street2: data.street2,
                city: data.city,
                state: data.state,
                zip: data.zip,
                country: data.country,
                phone: data.phone,
            });

            notification.success(
                'Address added',
                'Your new address has been saved to your account.'
            );
            router.back();

        } catch (error: any) {
            let errorMessage = 'An unexpected error occurred';

            // Handle specific API errors
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            // Handle validation errors
            if (error.response?.status === 400) {
                errorMessage = error.response.data?.message || 'Please check your address details and try again';
            }

            // Handle network errors
            if (error.message === 'Network Error') {
                errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            }

            notification.error(
                'Unable to save address',
                errorMessage,
                5000
            );

            // Show additional guidance for certain error types
            if (error.response?.status === 422) {
                notification.info(
                    'Address validation failed',
                    'Make sure your address is complete and formatted correctly.',
                    6000
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="relative bg-linear-to-br from-[rgb(var(--header-from))] via-[rgb(var(--header-via))] to-[rgb(var(--header-to))] text-white">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.35),transparent_60%)]" />
                <div className="relative container mx-auto px-4 py-8">
                    <button
                        onClick={() => router.back()}
                        className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </button>
                    <div className="mt-6 flex items-center gap-4">
                        <div className="rounded-full bg-white/10 p-3">
                            <MapPin className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold">Add New Address</h1>
                            <p className="mt-1 text-white/75">
                                Enter your delivery address details below.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl p-6">
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Full name"
                                    placeholder="John Doe"
                                    {...register('name')}
                                    maxLength={100}
                                    error={errors.name?.message}
                                />
                                <Input
                                    label="Phone number"
                                    placeholder="+1 415 555 0100"
                                    {...register('phone')}
                                    error={errors.phone?.message}
                                />
                                <div className="md:col-span-2">
                                    <Input
                                        label="Address line 1"
                                        placeholder="123 Main St"
                                        {...register('street1')}
                                        maxLength={200}
                                        error={errors.street1?.message}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Input
                                        label="Address line 2 (optional)"
                                        placeholder="Apt 4B"
                                        {...register('street2')}
                                        maxLength={200}
                                    />
                                </div>
                                <Input
                                    label="City"
                                    placeholder="New York"
                                    {...register('city')}
                                    maxLength={100}
                                    error={errors.city?.message}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                    <select
                                        {...register('state')}
                                        className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-hidden focus:ring-4 text-sm bg-white ${errors.state ? 'border-red-500 focus:border-red-600 focus:ring-red-500/20' : 'border-gray-200 focus:border-hprimary-500 focus:ring-hprimary-500/20'}`}
                                    >
                                        <option value="">Select state</option>
                                        {US_STATES.map(s => (
                                            <option key={s.abbr} value={s.abbr}>{s.name}</option>
                                        ))}
                                    </select>
                                    {errors.state && <p className="mt-2 text-sm text-red-600">{errors.state.message}</p>}
                                </div>
                                <Input
                                    label="ZIP code"
                                    placeholder="10001"
                                    {...register('zip')}
                                    maxLength={20}
                                    error={errors.zip?.message}
                                />
                                <Input
                                    label="Country"
                                    {...register('country')}
                                    disabled
                                    className="bg-gray-50 text-gray-500 cursor-not-allowed"
                                />
                            </div>
                            <div className="flex justify-end gap-4">
                                <Button
                                    type="button"
                                    variant="outline-solid"
                                    onClick={() => router.back()}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? 'Adding Address...' : 'Add Address'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}